/**
 * Safe in-app path redirection helper.
 *
 * Validates that a user-supplied `next` path is strictly an internal, relative
 * application path starting with a single '/' and cannot trigger open redirects,
 * protocol-relative redirects, scheme execution (e.g. javascript:), or browser path tricks.
 */
export function sanitizeNextPath(next: string | null | undefined, defaultPath: string): string {
  if (!next || typeof next !== "string") {
    return defaultPath
  }

  const trimmed = next.trim()
  if (!trimmed) {
    return defaultPath
  }

  // Must begin with a single '/' and NOT '//' or '/\'
  if (!trimmed.startsWith("/") || trimmed.startsWith("//") || trimmed.startsWith("/\\")) {
    return defaultPath
  }

  // Reject backslashes anywhere in the raw path (browsers like Chrome/Safari treat \ as / in redirects)
  if (trimmed.includes("\\")) {
    return defaultPath
  }

  // Reject ASCII control characters
  for (let i = 0; i < trimmed.length; i++) {
    const code = trimmed.charCodeAt(i)
    if (code <= 0x1F || code === 0x7F) {
      return defaultPath
    }
  }

  // Attempt URL decoding to catch percent-encoded bypasses (%2f%2f, %5c, %00, etc.)
  let decoded: string
  try {
    decoded = decodeURIComponent(trimmed)
  } catch {
    // Malformed percent-encoding
    return defaultPath
  }

  // Ensure decoded representation also starts with a single '/' and no backslashes
  if (!decoded.startsWith("/") || decoded.startsWith("//") || decoded.startsWith("/\\") || decoded.includes("\\")) {
    return defaultPath
  }

  // Reject control characters in decoded representation
  for (let i = 0; i < decoded.length; i++) {
    const code = decoded.charCodeAt(i)
    if (code <= 0x1F || code === 0x7F) {
      return defaultPath
    }
  }

  // Reject absolute protocol indicators or uri schemes
  if (decoded.includes("://") || /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(decoded.slice(1))) {
    return defaultPath
  }

  return trimmed
}
